import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import CircularProgress from '@mui/material/CircularProgress'
import type { ReactNode } from 'react'
import { cssTransition } from 'react-toastify'

// Shared toast presentation, consumed by both App.tsx's <ToastContainer> and
// the Storybook story. Single source for the transition + the global icon set.

// Sonner-style slide + fade (bottom-right). Keyframes live in
// assets/styles/toast.css; collapseDuration matches the 400ms motion.
export const toastTransition = cssTransition({
  enter: 'yogi-toast-in',
  exit: 'yogi-toast-out',
  collapseDuration: 400,
})

// Sonner per-type icon hues — the card stays neutral; only the icon carries color.
const ICON_COLOR = {
  success: 'hsl(140, 100%, 27%)',
  error: 'hsl(360, 100%, 45%)',
  info: 'hsl(210, 92%, 45%)',
  warning: 'hsl(31, 92%, 45%)',
} as const

const ICON_SX = { fontSize: 18, flexShrink: 0 } as const

type ToastIconArgs = {
  type?: string
  isLoading?: boolean
}

// Global icon override for <ToastContainer icon={...}>. Every toast renders an
// MUI icon; loading toasts get a round CircularProgress; default toasts get none.
export const renderToastIcon = ({
  type,
  isLoading,
}: ToastIconArgs): ReactNode => {
  if (isLoading) {
    return (
      <CircularProgress
        size={16}
        thickness={5}
        // disableShrink drops MUI's stroke-dasharray keyframe, leaving only
        // the compositor-driven `transform: rotate` on the root. The dash
        // animation is main-thread-only, so it froze (often at its 1px-arc
        // keyframe, i.e. invisible) whenever a heavy export blocked the
        // thread — this is what made the "Building PDF..." toast's spinner
        // stutter and vanish.
        disableShrink
        sx={{ color: '#616161', flexShrink: 0 }}
      />
    )
  }
  switch (type) {
    case 'success':
      return <CheckCircleIcon sx={{ ...ICON_SX, color: ICON_COLOR.success }} />
    case 'error':
      return <ErrorIcon sx={{ ...ICON_SX, color: ICON_COLOR.error }} />
    case 'info':
      return <InfoIcon sx={{ ...ICON_SX, color: ICON_COLOR.info }} />
    case 'warning':
      return <WarningIcon sx={{ ...ICON_SX, color: ICON_COLOR.warning }} />
    default:
      return null
  }
}
